/**
 * Canonical ids. CONTRACTS §1 is normative for this file.
 * Adding a seventh lesson or a sixth game is a plan change, not a lane decision.
 */

export const LESSON_IDS = ['p1', 'p2', 'p3', 'o1', 'o2', 'o3'] as const
export type LessonId = (typeof LESSON_IDS)[number]

/**
 * The five games of MASTERPLAN §9 plus the two ported from Diş Krallığı as games 6 and 7
 * (operator, 2026-09-08): `plak` is Plak Patlat, `kosu` is Fırça Koşusu. They are reached from
 * the Oyunlar sheet, not from a region; REGION_GAME still names one of the first five.
 * NEEDS KARDIS: CONTRACTS §1 still reads "exactly 5 game ids".
 */
export const GAME_IDS = [
  'sekerci',
  'disdostu',
  'dogruyanlis',
  'firtina',
  'nefes',
  'plak',
  'kosu',
] as const
export type GameId = (typeof GAME_IDS)[number]

export const WINDOWS = ['morning', 'evening', 'outside'] as const
export type BrushWindow = (typeof WINDOWS)[number]

/** The two windows that can be opened and closed by the clock. `outside` never closes. */
export const CLOSING_WINDOWS = ['morning', 'evening'] as const
export type ClosingWindow = (typeof CLOSING_WINDOWS)[number]

export const OUTCOMES = ['verified', 'partial', 'tried', 'nocam'] as const
export type Outcome = (typeof OUTCOMES)[number]

export const MODES = ['mini', 'core'] as const
export type Mode = (typeof MODES)[number]

export const AGE_BANDS = ['5-7', '8-12'] as const
export type AgeBand = (typeof AGE_BANDS)[number]

export const WINDOW_STATES = ['none', 'nocam', 'tried', 'partial', 'verified'] as const
export type WindowState = (typeof WINDOW_STATES)[number]

export const SLOTS = ['head', 'eyes', 'back', 'hand'] as const
export type Slot = (typeof SLOTS)[number]

export const DECOR_SLOTS = ['left', 'right', 'floor', 'wall'] as const
export type DecorSlot = (typeof DECOR_SLOTS)[number]

export const SHINE_STATES = ['parlak', 'normal', 'mat', 'uzgun'] as const
export type ShineState = (typeof SHINE_STATES)[number]

export const PET_STAGES = ['sut', 'buyuk', 'parlak'] as const
export type PetStage = (typeof PET_STAGES)[number]

/**
 * Badge ids in evaluation order (CONTENT §8, ASSETS §4). Fold rule 11 walks this list, so the
 * order is the reward order and must not be shuffled.
 *
 * TODO(open:badge-count) MASTERPLAN §8.5 tallies 26 badges by category and leaves `bilgin`
 * out of every category, while CONTENT §8 and ASSETS §4 both list it, which makes 27. The two
 * concrete lists agree, so 27 ships; KARDIS decides whether §8.5's tally or `bilgin` goes.
 */
export const BADGE_IDS = [
  'ilk-firca',
  'seri-3',
  'seri-7',
  'seri-14',
  'seri-30',
  'seri-60',
  'seri-100',
  'tam-gun-7',
  'firca-10',
  'firca-50',
  'firca-100',
  'firca-365',
  'ders-p1',
  'ders-p2',
  'ders-p3',
  'ders-o1',
  'ders-o2',
  'ders-o3',
  'bilgin',
  'avci',
  'gurme',
  'dedektif',
  /**
   * TODO(open:ritimci-id) the badge id and its `badge/ritimci` art predate Fırça Fırtınası
   * (the rhythm game it named is gone, W3-F). The name and hint in content/badges.json follow
   * the new game; the id stays until the art lane draws a storm badge and KARDIS renames it.
   */
  'ritimci',
  'baloncu',
  'bakici',
  'modaci',
  'dekorator',
  'hatirlatici',
] as const
export type BadgeId = (typeof BADGE_IDS)[number]

/**
 * Recap question ids (CONTRACTS §1, W3-OVERHAUL §2.4 and §2.6): one static question per lesson
 * in `content/recap.json` (`h-<lesson>`), and the generated daily questions of
 * `content/daily/<date>.json` (`d-<date>-<n>`, n = 1..6). Never a lesson id.
 */
export type StaticRecapQuestionId = `h-${LessonId}`
export type DailyRecapQuestionId = `d-${string}-${number}`
export type RecapQuestionId = StaticRecapQuestionId | DailyRecapQuestionId

export const STATIC_RECAP_QUESTION_IDS = ['h-p1', 'h-p2', 'h-p3', 'h-o1', 'h-o2', 'h-o3'] as const

export const RECAP_QUESTION_ID_RE = /^(?:h-(?:p1|p2|p3|o1|o2|o3)|d-\d{4}-\d{2}-\d{2}-[1-6])$/

export function isRecapQuestionId(value: string): value is RecapQuestionId {
  return RECAP_QUESTION_ID_RE.test(value)
}

export function recapQuestionIdOf(lessonId: LessonId): StaticRecapQuestionId {
  return `h-${lessonId}`
}

/**
 * Challenge template ids of `content/challenges.json` (W3-OVERHAUL §4.2). Three brush templates
 * (one per window shape), the question template and its `ders` fallback, one game template.
 * A day's three görevler are one of each kind; `GorevId` is what `gorev.today.done` lists.
 */
export const CHALLENGE_IDS = [
  'g-brush-sabah',
  'g-brush-aksam',
  'g-brush-tamgun',
  'g-soru',
  'g-ders',
  'g-oyun',
] as const
export type GorevId = (typeof CHALLENGE_IDS)[number]

export const GOREV_KINDS = ['brush', 'question', 'game'] as const
export type GorevKind = (typeof GOREV_KINDS)[number]

export function isGorevId(value: string): value is GorevId {
  return (CHALLENGE_IDS as readonly string[]).includes(value)
}

/** Region 0 is Yuva and has no lesson. Regions 1 to 6 map to the lessons in order. */
export const REGION_COUNT = 6
export const LESSON_REGION: Record<LessonId, number> = {
  p1: 1,
  p2: 2,
  p3: 3,
  o1: 4,
  o2: 5,
  o3: 6,
}
export const REGION_LESSON: Record<number, LessonId> = {
  1: 'p1',
  2: 'p2',
  3: 'p3',
  4: 'o1',
  5: 'o2',
  6: 'o3',
}

/** The game each region introduces. Region 6 introduces none (MASTERPLAN §4.2). */
export const REGION_GAME: Record<number, GameId | null> = {
  1: 'sekerci',
  2: 'disdostu',
  3: 'firtina',
  4: 'dogruyanlis',
  5: 'nefes',
  6: null,
}

/**
 * Content ids (food, habit card, badge, shop item) and asset ids.
 *
 * TODO(open:asset-id-charset) CONTRACTS §1 writes the charset as `[a-z][a-z0-9_]*`, but every
 * id actually listed in ASSETS §4 to §6 uses dashes and slashes (`cos/hat-ormanci`,
 * `lesson/p1/hero`). The regexes below follow ASSETS, which is the real manifest. Turkish
 * letters are not allowed in ids; the display names live in content/ JSON.
 */
export const CONTENT_ID_RE = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/
export const ASSET_ID_RE = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*(?:\/[a-z0-9]+(?:-[a-z0-9]+)*)*$/

export function isLessonId(value: string): value is LessonId {
  return (LESSON_IDS as readonly string[]).includes(value)
}

export function isGameId(value: string): value is GameId {
  return (GAME_IDS as readonly string[]).includes(value)
}

export function isBadgeId(value: string): value is BadgeId {
  return (BADGE_IDS as readonly string[]).includes(value)
}
